import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
// @mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  Divider,
  Box,
  IconButton,
  Stack,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
// @iconify
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import addFill from '@iconify/icons-eva/plus-fill';
import removeFill from '@iconify/icons-eva/minus-fill';
// validation schemas
import { tariffValidationSchema } from 'src/utils/validationSchemas';
// constants
import {
  TARIFF_FORM_INITIAL_VALUES,
  OCPI_COUNTRIES,
  OCPI_TARIFF_TYPES_ARRAY,
  OCPI_CURRENCIES,
  OCPI_ENERGY_CATEGORIES,
  OCPI_TARIFF_ELEMENTS,
  OCPI_TARIFF_RESTRICTIONS
} from 'src/utils/ocpi.constants';
import { Loader } from 'src/components/Loader';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 2,
    maxWidth: 900,
    width: '100%'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2)
}));

const TariffForm = ({
  open,
  onClose,
  onSubmit,
  initialValues = null,
  isEdit = false
}) => {
  const formValues = initialValues || TARIFF_FORM_INITIAL_VALUES;

  const handleSubmit = (values, { setSubmitting }) => {
    // Process form data
    const tariffData = {
      ...values,
      description: values.description || null,
      min_price: values.min_price || 0,
      max_price: values.max_price || null
    };
    onSubmit(tariffData, setSubmitting);
  };

  const addPriceComponent = (setFieldValue, values, elementIndex) => {
    const newElements = [...values.elements];
    if (!newElements[elementIndex].price_components) {
      newElements[elementIndex].price_components = [];
    }
    newElements[elementIndex].price_components.push({
      type: 'ENERGY',
      price: 0,
      step_size: 1
    });
    setFieldValue('elements', newElements);
  };

  const removePriceComponent = (setFieldValue, values, elementIndex, componentIndex) => {
    const newElements = [...values.elements];
    newElements[elementIndex].price_components.splice(componentIndex, 1);
    setFieldValue('elements', newElements);
  };

  const addElement = (setFieldValue, values) => {
    const newElements = [...values.elements, {
      price_components: [{
        type: 'ENERGY',
        price: 0,
        step_size: 1
      }]
    }];
    setFieldValue('elements', newElements);
  };

  const removeElement = (setFieldValue, values, elementIndex) => {
    const newElements = values.elements.filter((_, index) => index !== elementIndex);
    setFieldValue('elements', newElements);
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Formik
        initialValues={formValues}
        validationSchema={tariffValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {
          return (
            <Form className='relative'>
              <Loader
                variant="top"
                open={isSubmitting}
                message={isEdit ? "Updating Tariff..." : "Creating Tariff..."}
                description="Please wait while we process your request"
                showLinearProgress={isSubmitting}
              />
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">
                    {isEdit ? 'Edit OCPI Tariff' : 'Create New OCPI Tariff'}
                  </Typography>
                  <IconButton onClick={onClose} size="small">
                    <Icon icon={closeFill} />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent dividers>
                <Grid container spacing={3}>
                  {/* Basic Information Section */}
                  <Grid item xs={12}>
                    <SectionTitle variant="subtitle1">
                      Basic Information
                    </SectionTitle>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth error={touched.countryCode && errors.countryCode}>
                      <InputLabel>Country Code *</InputLabel>
                      <Select
                        name="countryCode"
                        value={values.countryCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Country Code *"
                      >
                        {OCPI_COUNTRIES.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.code} - {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.countryCode && errors.countryCode && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.countryCode}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="partyId"
                      label="Party ID *"
                      value={values.partyId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.partyId && errors.partyId}
                      helperText={touched.partyId && errors.partyId}
                      inputProps={{ maxLength: 3 }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="id"
                      label="Tariff ID *"
                      value={values.id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.id && errors.id}
                      helperText={touched.id && errors.id}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth error={touched.currency && errors.currency}>
                      <InputLabel>Currency *</InputLabel>
                      <Select
                        name="currency"
                        value={values.currency}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Currency *"
                      >
                        {OCPI_CURRENCIES.map((currency) => (
                          <MenuItem key={currency.code} value={currency.code}>
                            {currency.code} - {currency.symbol} {currency.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.currency && errors.currency && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.currency}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth error={touched.type && errors.type}>
                      <InputLabel>Tariff Type *</InputLabel>
                      <Select
                        name="type"
                        value={values.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Tariff Type *"
                      >
                        {OCPI_TARIFF_TYPES_ARRAY.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.type && errors.type && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.type}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth error={touched.energy_category && errors.energy_category}>
                      <InputLabel>Energy Category *</InputLabel>
                      <Select
                        name="energy_category"
                        value={values.energy_category}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Energy Category *"
                      >
                        {OCPI_ENERGY_CATEGORIES.map((category) => (
                          <MenuItem key={category.code} value={category.code}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.energy_category && errors.energy_category && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.energy_category}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth error={touched.restrictions && errors.restrictions}>
                      <InputLabel>Restrictions</InputLabel>
                      <Select
                        name="restrictions"
                        value={values.restrictions}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Restrictions"
                      >
                        {OCPI_TARIFF_RESTRICTIONS.map((restriction) => (
                          <MenuItem key={restriction.code} value={restriction.code}>
                            {restriction.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.restrictions && errors.restrictions && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.restrictions}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="description"
                      label="Description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      multiline
                      rows={2}
                      placeholder="Optional description for this tariff"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="min_price"
                      label="Minimum Price"
                      type="number"
                      value={values.min_price}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="0.00"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name="max_price"
                      label="Maximum Price"
                      type="number"
                      value={values.max_price || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="No limit"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="valid"
                          checked={values.valid}
                          onChange={(e) => {
                            handleChange({
                              target: {
                                name: 'valid',
                                value: e.target.checked
                              }
                            });
                          }}
                          color="success"
                        />
                      }
                      label="Tariff is valid"
                    />
                  </Grid>

                  {/* Pricing Elements Section */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <SectionTitle variant="subtitle1">
                      Pricing Elements
                    </SectionTitle>
                  </Grid>

                  {values.elements.map((element, elementIndex) => (
                    <Grid item xs={12} key={elementIndex}>
                      <Box sx={{ 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: 'grey.300', 
                        borderRadius: 1,
                        backgroundColor: 'grey.50'
                      }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="primary">
                            Element {elementIndex + 1}
                          </Typography>
                          {values.elements.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() => removeElement(setFieldValue, values, elementIndex)}
                              color="error"
                            >
                              <Icon icon={removeFill} />
                            </IconButton>
                          )}
                        </Stack>

                        {element.price_components?.map((component, componentIndex) => (
                          <Box key={componentIndex} sx={{ mb: 2, p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Price Component {componentIndex + 1}
                              </Typography>
                              {element.price_components.length > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => removePriceComponent(setFieldValue, values, elementIndex, componentIndex)}
                                  color="error"
                                >
                                  <Icon icon={removeFill} />
                                </IconButton>
                              )}
                            </Stack>

                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <FormControl fullWidth>
                                  <InputLabel>Type</InputLabel>
                                  <Select
                                    value={component.type || 'ENERGY'}
                                    onChange={(e) => {
                                      const newElements = [...values.elements];
                                      newElements[elementIndex].price_components[componentIndex].type = e.target.value;
                                      setFieldValue('elements', newElements);
                                    }}
                                    label="Type"
                                  >
                                    {OCPI_TARIFF_ELEMENTS.map((elementType) => (
                                      <MenuItem key={elementType.code} value={elementType.code}>
                                        {elementType.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={4}>
                                <TextField
                                  fullWidth
                                  label="Price"
                                  type="number"
                                  value={component.price || 0}
                                  onChange={(e) => {
                                    const newElements = [...values.elements];
                                    newElements[elementIndex].price_components[componentIndex].price = parseFloat(e.target.value);
                                    setFieldValue('elements', newElements);
                                  }}
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField
                                  fullWidth
                                  label="Step Size"
                                  type="number"
                                  value={component.step_size || 1}
                                  onChange={(e) => {
                                    const newElements = [...values.elements];
                                    newElements[elementIndex].price_components[componentIndex].step_size = parseFloat(e.target.value);
                                    setFieldValue('elements', newElements);
                                  }}
                                  inputProps={{ min: 0.1, step: 0.1 }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        ))}

                        <Button
                          variant="outlined"
                          startIcon={<Icon icon={addFill} />}
                          onClick={() => addPriceComponent(setFieldValue, values, elementIndex)}
                          size="small"
                        >
                          Add Price Component
                        </Button>
                      </Box>
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon icon={addFill} />}
                      onClick={() => addElement(setFieldValue, values)}
                      fullWidth
                    >
                      Add Pricing Element
                    </Button>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} color="inherit">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isEdit ? 'Update Tariff' : 'Create Tariff'}
                </Button>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </StyledDialog>
  );
};

TariffForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool
};

export default TariffForm;
